import { averageMoodScore } from './diaryStats';
import { computeAllInsights, mostActiveDays, routeDistanceByType } from './insights';
import { mediaTotal } from './mediaAggregates';
import { t } from './i18n';

export const COMPARABLE_METRICS = [
  { key: 'checkins', label: 'Check-ins', source: 'checkins', example: '120 vs 98' },
  { key: 'distanceKm', label: t('web.compare.distanceKm'), source: 'health + moves + googleMaps', example: '340 km vs 265 km' },
  { key: 'activeDays', label: 'Dias ativos', source: 'todas as fontes', example: '18 vs 11' },
  { key: 'musicPlays', label: t('web.compare.musicPlays'), source: 'spotify + appleMusic', example: '540 vs 210' },
  { key: 'movies', label: 'Filmes', source: 'movies', example: '8 vs 3' },
  { key: 'episodes', label: t('web.compare.episodes'), source: 'shows', example: '22 vs 11' },
  { key: 'concerts', label: 'Shows', source: 'concerts', example: '4 vs 1' },
  { key: 'diaryEntries', label: t('web.compare.diaryEntries'), source: 'diary', example: '16 vs 9' },
  { key: 'avgMood', label: t('web.compare.avgMood'), source: 'diary', example: '4.1 vs 3.6' },
  { key: 'cities', label: 'Cidades visitadas', source: 'checkins + concerts', example: '6 vs 2' },
];

const EMPTY_DATA = {
  checkins: [],
  health: [],
  moves: [],
  movies: [],
  shows: [],
  diary: [],
  concerts: [],
  googleMaps: [],
  spotify: [],
  appleMusic: [],
  photos: [],
};

const clampDateString = (value) => {
  if (!value) return null;
  const date = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
};

const extractDate = (item) => clampDateString(
  item?.date ||
  item?.entry_date ||
  item?.watched_at ||
  item?.event_date ||
  item?.played_at ||
  item?.route_date
);

const extractRouteDate = (route) => {
  const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
  return clampDateString(firstPoint.route_date || firstPoint.date);
};

const isWithinRange = (date, startDate, endDate) => {
  if (!date) return false;
  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
};

export const sliceDataByDateRange = (data = {}, range = {}) => {
  const startDate = clampDateString(range.startDate);
  const endDate = clampDateString(range.endDate);

  const sliceListOrAggregate = (value, dateField = 'date') => {
    if (value && typeof value === 'object' && !Array.isArray(value) && value._aggregate) {
      const byDay = (value.byDay || []).filter((row) =>
        isWithinRange(clampDateString(row.date), startDate, endDate)
      );
      const total = byDay.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
      return {
        ...value,
        byDay,
        total,
      };
    }
    return (value || []).filter((item) => isWithinRange(extractDate(item), startDate, endDate));
  };

  const sliced = {
    ...EMPTY_DATA,
    ...data,
    checkins: (data.checkins || []).filter((item) => isWithinRange(extractDate(item), startDate, endDate)),
    health: (data.health || []).filter((item) => isWithinRange(extractRouteDate(item), startDate, endDate)),
    moves: (data.moves || []).filter((item) => isWithinRange(extractRouteDate(item), startDate, endDate)),
    movies: sliceListOrAggregate(data.movies),
    shows: sliceListOrAggregate(data.shows),
    diary: (data.diary || []).filter((item) => isWithinRange(extractDate(item), startDate, endDate)),
    concerts: (data.concerts || []).filter((item) => isWithinRange(extractDate(item), startDate, endDate)),
    googleMaps: (data.googleMaps || []).filter((item) => isWithinRange(extractRouteDate(item), startDate, endDate)),
    spotify: sliceListOrAggregate(data.spotify),
    appleMusic: sliceListOrAggregate(data.appleMusic),
    photos: (data.photos || []).filter((item) => isWithinRange(extractDate(item), startDate, endDate)),
  };

  if (data.music && typeof data.music === 'object' && data.music._aggregate) {
    sliced.music = sliceListOrAggregate(data.music);
  }

  return sliced;
};

const sumObjectValues = (input = {}) => Object.values(input).reduce((sum, value) => sum + (Number(value) || 0), 0);

const computePeriodMetrics = (data = {}) => {
  const insights = computeAllInsights(data);
  return {
    checkins: (data.checkins || []).length,
    distanceKm: sumObjectValues(routeDistanceByType(data)),
    activeDays: mostActiveDays(data, { topN: Number.MAX_SAFE_INTEGER }).length,
    musicPlays: mediaTotal(data.spotify) + mediaTotal(data.appleMusic),
    movies: mediaTotal(data.movies),
    episodes: mediaTotal(data.shows),
    concerts: (data.concerts || []).length,
    diaryEntries: (data.diary || []).length,
    avgMood: averageMoodScore(data.diary || []),
    cities: (insights.cities || []).length,
  };
};

const computeDiff = (valueA, valueB) => {
  const a = Number(valueA) || 0;
  const b = Number(valueB) || 0;
  const absolute = a - b;
  if (b === 0) {
    return {
      absolute,
      percent: a === 0 ? 0 : null,
    };
  }
  return {
    absolute,
    percent: (absolute / b) * 100,
  };
};

export const comparePeriods = (dataA = {}, dataB = {}) => {
  const metricsA = computePeriodMetrics(dataA);
  const metricsB = computePeriodMetrics(dataB);

  const comparison = COMPARABLE_METRICS.reduce((acc, metric) => {
    acc[metric.key] = {
      key: metric.key,
      label: metric.label,
      source: metric.source,
      valueA: metricsA[metric.key],
      valueB: metricsB[metric.key],
      ...computeDiff(metricsA[metric.key], metricsB[metric.key]),
    };
    return acc;
  }, {});

  return {
    metricsA,
    metricsB,
    comparison,
  };
};

export const compareLoadedPeriods = (data = {}, rangeA, rangeB) => comparePeriods(
  sliceDataByDateRange(data, rangeA),
  sliceDataByDateRange(data, rangeB),
);

export const formatComparableValue = (key, value) => {
  const num = Number(value) || 0;

  if (key === 'distanceKm') {
    if (num < 0.1) return '0 km';
    if (num < 1) return `${Math.round(num * 1000)} m`;
    if (num < 10) return `${num.toFixed(1)} km`;
    return `${Math.round(num)} km`;
  }

  if (key === 'avgMood') {
    return num.toFixed(1);
  }

  return String(Math.trunc(num));
};
